import { ChangeEvent, useRef } from 'react'
import { Button, Box, Grid, Typography, Card, CardMedia, CardActionArea, Divider } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'

interface ImageUploaderProps {
  onImageLoad: (image: HTMLImageElement) => void
}

const STARTER_IMAGES = [
  { name: 'Golden Retriever', path: '/starter-images/Happy golden retriever and toy ball.png' },
  { name: 'Kitten in Basket', path: '/starter-images/Relaxing kitten in a wicker basket.png' },
  { name: 'Tropical Beach', path: '/starter-images/Tropical beach in stained glass.png' },
  { name: 'Sunflowers', path: '/starter-images/sunflowers.jpeg' },
  { name: 'JigSoli', path: '/starter-images/jigsoli.jpeg' },
]

function ImageUploader({ onImageLoad }: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file')
      return
    }

    // Create image element and load the file
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        onImageLoad(img)
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  }

  const handleStarterImageClick = (imagePath: string) => {
    const img = new Image()
    img.onload = () => {
      onImageLoad(img)
    }
    img.src = imagePath
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STARTER_IMAGES.map((image) => (
          <Grid item xs={6} sm={4} md={2.4} key={image.path}>
            <Card
              sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardActionArea onClick={() => handleStarterImageClick(image.path)}>
                <CardMedia
                  component="img"
                  height="120"
                  image={image.path}
                  alt={image.name}
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, textAlign: 'center', bgcolor: 'background.paper' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    {image.name}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" color="text.secondary">
          OR
        </Typography>
      </Divider>

      <Box sx={{ textAlign: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <Button
          variant="contained"
          size="large"
          startIcon={<CloudUploadIcon />}
          onClick={handleButtonClick}
          sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          Upload Your Own Image
        </Button>
      </Box>
    </Box>
  )
}

export default ImageUploader
